import { ProcessType } from '../../../types/response'
import { cn } from '@/lib/utils'
import {
    Download,
    RotateCcw,
    Upload,
    Heart,
    Truck,
    ArrowRightLeft,
    ClipboardCheck,
} from 'lucide-react'

interface ProcessTypeSelectorProps {
    selectedType: ProcessType
    onTypeChange: (type: ProcessType) => void
    disabled?: boolean
}

interface ProcessTypeOption {
    value: ProcessType
    label: string
    description: string
    icon: React.ElementType
}

const PROCESS_TYPES: ProcessTypeOption[] = [
    {
        value: 'import_purchase',
        label: 'Import - Purchase',
        description: 'Import items from supplier purchase',
        icon: Download,
    },
    {
        value: 'import_return',
        label: 'Import - Return',
        description: 'Import items returned from customer',
        icon: RotateCcw,
    },
    {
        value: 'export_sale',
        label: 'Export - Sale',
        description: 'Export items for sale to customer',
        icon: Upload,
    },
    {
        value: 'export_loan',
        label: 'Export - Loan',
        description: 'Export items as loan to customer',
        icon: Heart,
    },
    {
        value: 'export_delivery',
        label: 'Export - Delivery',
        description: 'Export items for delivery',
        icon: Truck,
    },
    {
        value: 'internal_transfer',
        label: 'Internal Transfer',
        description: 'Transfer items between locations',
        icon: ArrowRightLeft,
    },
    {
        value: 'inventory_check',
        label: 'Inventory Check',
        description: 'Perform inventory verification',
        icon: ClipboardCheck,
    },
]

const ProcessTypeSelector = ({
    selectedType,
    onTypeChange,
    disabled = false,
}: ProcessTypeSelectorProps) => {
    return (
        <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
            data-tour="warehouse-process-type-selector"
        >
            {PROCESS_TYPES.map((processType) => {
                const Icon = processType.icon
                const isSelected = selectedType === processType.value

                return (
                    <button
                        key={processType.value}
                        type="button"
                        disabled={disabled}
                        onClick={() => onTypeChange(processType.value)}
                        className={cn(
                            'flex items-start gap-3 p-4 rounded-lg border-2 text-left transition-all',
                            'hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20',
                            isSelected
                                ? 'border-primary bg-primary/5'
                                : 'border-border bg-background',
                            disabled &&
                                'opacity-50 cursor-not-allowed hover:border-border'
                        )}
                    >
                        <div
                            className={cn(
                                'flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center',
                                isSelected
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-muted text-muted-foreground'
                            )}
                        >
                            <Icon className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                            <div
                                className={cn(
                                    'font-medium text-sm',
                                    isSelected
                                        ? 'text-primary'
                                        : 'text-foreground'
                                )}
                            >
                                {processType.label}
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                                {processType.description}
                            </div>
                        </div>
                    </button>
                )
            })}
        </div>
    )
}

export default ProcessTypeSelector
